/**
 * Перезагрузка вкладки, пережившей выкат новой версии.
 *
 * ЧТО ЛОМАЕТСЯ. Открытая вкладка держит `index.html` со СТАРЫМИ хешами чанков. Выкат
 * заменяет каталог `dist` целиком, старые чанки исчезают, и первая же ленивая загрузка
 * (переход на экран, открытый впервые) падает с
 * `TypeError: Failed to fetch dynamically imported module: /assets/…-Cyv_I9H0.js`.
 * Оператор видит БЕЛЫЙ ЭКРАН без единого слова. Наблюдалось живьём 2026-09-03.
 *
 * ЭТО НЕ ИЗДЕРЖКА СТЕНДА. У оператора при штатном обновлении версии происходит ровно то
 * же: держал вкладку открытой, вышла новая версия, нажал пункт меню — белый экран.
 * Разница только в частоте выкатов.
 *
 * ПОЧЕМУ ПЕРЕЗАГРУЗКА, А НЕ СООБЩЕНИЕ С КНОПКОЙ. К моменту ошибки роутер уже ушёл на новый
 * маршрут, а его код не загрузился — показывать что-либо не на чем, экран пуст. Единственное
 * состояние, из которого оператор может продолжить работу, — свежий `index.html`.
 *
 * ЗАЩИТА ОТ ПЕТЛИ ОБЯЗАТЕЛЬНА. Если чанк недоступен по другой причине (сеть, битый прокси,
 * ошибка раздачи), перезагрузка не поможет и будет крутиться бесконечно — а это хуже
 * исходного дефекта: белый экран хотя бы статичен. Поэтому перезагрузка ровно ОДНА на
 * вкладку: метка в `sessionStorage`, и при повторной неудаче ошибка отдаётся дальше,
 * чтобы её увидел обработчик ошибок и человек.
 */

const RELOAD_MARK = 'wn:stale-chunk-reloaded';

/** Признаки «модуль не загрузился», по браузерам. Текст различается, шанса на один нет. */
const STALE_CHUNK_PATTERNS = [
  'failed to fetch dynamically imported module', // Chrome, Edge
  'error loading dynamically imported module', // Safari
  'importing a module script failed', // Firefox
  'unable to preload css', // Vite, стили ленивого маршрута
];

function looksLikeStaleChunk(reason: unknown): boolean {
  const text =
    reason instanceof Error
      ? `${reason.name} ${reason.message}`
      : typeof reason === 'string'
        ? reason
        : '';
  const lower = text.toLowerCase();
  return STALE_CHUNK_PATTERNS.some((pattern) => lower.includes(pattern));
}

/**
 * Уже перезагружались в этой вкладке?
 *
 * `sessionStorage` может быть недоступен (приватный режим, запрет хранилища) — тогда
 * обращение БРОСАЕТ, а не возвращает пусто. Падение здесь означало бы, что обработчик
 * ошибки сам стал ошибкой, поэтому недоступное хранилище трактуется как «перезагрузка
 * уже была»: лучше показать ошибку, чем уйти в петлю без счётчика.
 */
function alreadyReloaded(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_MARK) === '1';
  } catch {
    return true;
  }
}

function markReloaded(): boolean {
  try {
    sessionStorage.setItem(RELOAD_MARK, '1');
    return true;
  } catch {
    return false;
  }
}

/** Снимает метку после успешной загрузки: следующий выкат должен лечиться так же. */
export function clearStaleChunkMark(): void {
  try {
    sessionStorage.removeItem(RELOAD_MARK);
  } catch {
    // Хранилище недоступно — метки и не было, снимать нечего.
  }
}

export interface StaleChunkOptions {
  /** Подменяется в тестах: `location.reload` в jsdom не перезагружает и не наблюдаем. */
  reload?: () => void;
  /** Подменяется в тестах: сюда уходит сообщение для оператора. */
  notify?: (message: string) => void;
}

/**
 * Возвращает `true`, если ошибка распознана как устаревший чанк и перезагрузка назначена.
 * `false` — ошибка чужая либо перезагрузка уже была; вызывающий обязан пропустить её дальше.
 */
export function handlePossibleStaleChunk(
  reason: unknown,
  options: StaleChunkOptions = {},
): boolean {
  if (!looksLikeStaleChunk(reason)) {
    return false;
  }
  if (alreadyReloaded()) {
    return false;
  }
  if (!markReloaded()) {
    return false;
  }

  options.notify?.('Приложение обновилось. Страница будет перезагружена.');
  (options.reload ?? (() => window.location.reload()))();
  return true;
}

/**
 * Ставит слушатели на глобальные каналы, по которым приходит эта ошибка.
 *
 * Каналов ДВА, и одного мало: `vite:preloadError` — событие самого Vite для ленивых
 * импортов, но оно не покрывает случай, когда импорт зовут в обход его хелпера. Второй
 * канал — необработанный отказ промиса, куда падает `import()` из роутера.
 */
export function installStaleChunkGuard(options: StaleChunkOptions = {}): () => void {
  const onPreloadError = (event: Event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    if (handlePossibleStaleChunk(payload, options)) {
      event.preventDefault();
    }
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    if (handlePossibleStaleChunk(event.reason, options)) {
      event.preventDefault();
    }
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('vite:preloadError', onPreloadError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
